import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { 
  Music, CalendarDays, Image, Link2, Home, LogOut, 
  Plus, Trash2, Upload, Save, Settings, Send, Mail, Check, X, Clock, Mic2, Play, Pause,
  BarChart3, ShoppingBag, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import AdminAnalytics from './AdminAnalytics';
import AdminProducts from './AdminProducts';
import AdminOrders from './AdminOrders';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/dj-shadow-logo.png";

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    } else {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin/login');
  };

  if (!isAuthenticated) return null;

  const navItems = [
    { icon: BarChart3, label: 'Analytics', path: '/admin' },
    { icon: Music, label: 'Mixes', path: '/admin/mixes' },
    { icon: CalendarDays, label: 'Events', path: '/admin/events' },
    { icon: Image, label: 'Photos', path: '/admin/photos' },
    { icon: Mic2, label: 'Submissions', path: '/admin/submissions' },
    { icon: ShoppingBag, label: 'Products', path: '/admin/products' },
    { icon: Package, label: 'Orders', path: '/admin/orders' },
    { icon: Send, label: 'Bookings', path: '/admin/bookings' },
    { icon: Link2, label: 'Social Links', path: '/admin/social' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Sidebar */}
      <aside className="w-64 glass border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DJ Shadow" className="w-10 h-10 rounded-lg" />
            <div>
              <p className="font-bold">DJ Shadow</p>
              <p className="text-xs text-zinc-500">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/admin'}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                ${isActive 
                  ? 'bg-[#D9F99D] text-black font-bold' 
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'}
              `}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <NavLink to="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-white hover:bg-white/5">
            <Home className="w-5 h-5" />
            <span>View Site</span>
          </NavLink>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <Routes>
          <Route index element={<AdminAnalytics />} />
          <Route path="mixes" element={<AdminMixes />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="photos" element={<AdminPhotos />} />
          <Route path="submissions" element={<AdminSubmissions />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="social" element={<AdminSocial />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
};

// Admin Mixes Page
const AdminMixes = () => {
  const [mixes, setMixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [formData, setFormData] = useState({
    title: '', description: '', genre: '', duration: '', cover_url: ''
  });
  const [audioFile, setAudioFile] = useState(null);

  // Chunk size: 2MB for faster uploads while staying under proxy limits
  const CHUNK_SIZE = 2 * 1024 * 1024;

  useEffect(() => {
    fetchMixes();
  }, []);

  const fetchMixes = async () => {
    try {
      const response = await axios.get(`${API_URL}/mixes`);
      setMixes(response.data);
    } catch (error) {
      console.error('Failed to fetch mixes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.title || !audioFile) {
      toast.error('Title and audio file are required');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const totalChunks = Math.ceil(audioFile.size / CHUNK_SIZE);
      
      // Step 1: Initialize upload
      setUploadStatus('Initializing upload...');
      const initResponse = await axios.post(`${API_URL}/admin/mixes/upload/init`, {
        filename: audioFile.name,
        total_chunks: totalChunks,
        file_size: audioFile.size
      });
      
      const uploadId = initResponse.data.upload_id;
      
      // Step 2: Upload chunks
      setUploadStatus('Uploading file...');
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, audioFile.size);
        const chunk = audioFile.slice(start, end);
        
        const chunkData = new FormData();
        chunkData.append('chunk', chunk);
        
        await axios.post(
          `${API_URL}/admin/mixes/upload/chunk/${uploadId}/${i}`,
          chunkData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
      }
      
      // Step 3: Complete upload and create mix
      setUploadStatus('Finalizing...');
      const completeData = new FormData();
      completeData.append('title', formData.title);
      completeData.append('description', formData.description || '');
      completeData.append('genre', formData.genre || '');
      completeData.append('duration', formData.duration || '0');
      completeData.append('cover_url', formData.cover_url || '');
      
      await axios.post(
        `${API_URL}/admin/mixes/upload/complete/${uploadId}`,
        completeData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadProgress(100);
      toast.success('Mix uploaded successfully!');
      setFormData({ title: '', description: '', genre: '', duration: '', cover_url: '' });
      setAudioFile(null);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      fetchMixes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload mix');
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const handleDelete = async (mixId) => {
    if (!window.confirm('Are you sure you want to delete this mix?')) return;
    try {
      await axios.delete(`${API_URL}/admin/mixes/${mixId}`);
      toast.success('Mix deleted');
      fetchMixes();
    } catch (error) {
      toast.error('Failed to delete mix');
    }
  };

  // Add mix via external URL
  const [urlFormData, setUrlFormData] = useState({
    title: '', description: '', genre: '', audio_url: '', cover_url: ''
  });
  const [addingUrl, setAddingUrl] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'url'

  const handleAddByUrl = async (e) => {
    e.preventDefault();
    if (!urlFormData.title || !urlFormData.audio_url) {
      toast.error('Title and Audio URL are required');
      return;
    }
    setAddingUrl(true);
    try {
      await axios.post(`${API_URL}/admin/mixes/external`, urlFormData);
      toast.success('Mix added successfully!');
      setUrlFormData({ title: '', description: '', genre: '', audio_url: '', cover_url: '' });
      fetchMixes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mix');
    } finally {
      setAddingUrl(false);
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Manage Mixes</h1>

      {/* Tab Selector */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'upload' 
              ? 'bg-[#D9F99D] text-black' 
              : 'bg-white/5 text-zinc-400 hover:bg-white/10'
          }`}
        >
          Upload File
        </button>
        <button
          onClick={() => setActiveTab('url')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'url' 
              ? 'bg-[#D9F99D] text-black' 
              : 'bg-white/5 text-zinc-400 hover:bg-white/10'
          }`}
        >
          Add by URL (Recommended)
        </button>
      </div>

      {/* Upload Form */}
      {activeTab === 'upload' && (
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#D9F99D]" />
          Upload New Mix
        </h2>
        <p className="text-zinc-400 text-sm mb-4">For large files, use "Add by URL" instead for better reliability.</p>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Mix title"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Genre</Label>
              <Input
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                placeholder="e.g., House, Techno"
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the mix..."
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duration (seconds)</Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="3600"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Cover Image URL</Label>
              <Input
                value={formData.cover_url}
                onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                placeholder="https://..."
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Audio File (MP3) *</Label>
            <Input
              type="file"
              accept="audio/*"
              onChange={(e) => setAudioFile(e.target.files[0])}
              className="bg-black/20 border-white/10"
              data-testid="audio-file-input"
            />
            {audioFile && (
              <p className="text-xs text-zinc-400">
                Selected: {audioFile.name} ({(audioFile.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            )}
          </div>
          
          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">{uploadStatus}</span>
                <span className="text-[#D9F99D]">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-[#D9F99D] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
          
          <Button 
            type="submit" 
            disabled={uploading}
            className="bg-[#D9F99D] text-black hover:bg-[#BEF264]"
            data-testid="upload-mix-button"
          >
            {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Mix'}
          </Button>
        </form>
      </div>
      )}

      {/* Add by URL Form */}
      {activeTab === 'url' && (
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#D9F99D]" />
          Add Mix by URL
        </h2>
        <p className="text-zinc-400 text-sm mb-4">
          Upload your audio file to a hosting service (Dropbox, Google Drive, etc.) and paste the direct link here.
        </p>
        <form onSubmit={handleAddByUrl} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={urlFormData.title}
                onChange={(e) => setUrlFormData({ ...urlFormData, title: e.target.value })}
                placeholder="Mix title"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Genre</Label>
              <Input
                value={urlFormData.genre}
                onChange={(e) => setUrlFormData({ ...urlFormData, genre: e.target.value })}
                placeholder="e.g., Hip-Hop, Latin"
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Audio URL * (direct link to MP3/M4A)</Label>
            <Input
              value={urlFormData.audio_url}
              onChange={(e) => setUrlFormData({ ...urlFormData, audio_url: e.target.value })}
              placeholder="https://example.com/your-mix.mp3"
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={urlFormData.description}
              onChange={(e) => setUrlFormData({ ...urlFormData, description: e.target.value })}
              placeholder="Describe the mix..."
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Cover Image URL</Label>
            <Input
              value={urlFormData.cover_url}
              onChange={(e) => setUrlFormData({ ...urlFormData, cover_url: e.target.value })}
              placeholder="https://..."
              className="bg-black/20 border-white/10"
            />
          </div>
          <Button 
            type="submit" 
            disabled={addingUrl}
            className="bg-[#D9F99D] text-black hover:bg-[#BEF264]"
          >
            {addingUrl ? 'Adding...' : 'Add Mix'}
          </Button>
        </form>
      </div>
      )}

      {/* Mixes List */}
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">All Mixes ({mixes.length})</h2>
        {mixes.length > 0 ? (
          <div className="space-y-3">
            {mixes.map((mix) => (
              <div key={mix.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div>
                  <p className="font-bold">{mix.title}</p>
                  <p className="text-sm text-zinc-400">{mix.genre || 'No genre'} • {mix.plays_count} plays</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(mix.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">No mixes uploaded yet</p>
        )}
      </div>
    </div>
  );
};

// Admin Events Page
const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', venue: '', location: '', date: '', time: '', description: '', ticket_url: '', cover_url: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/all`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.venue || !formData.location || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/events`, formData);
      toast.success('Event created!');
      setFormData({ title: '', venue: '', location: '', date: '', time: '', description: '', ticket_url: '', cover_url: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/admin/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Manage Events</h1>

      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#D9F99D]" />
          Add New Event
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Summer Rooftop Party"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Venue *</Label>
              <Input
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                placeholder="e.g., Club XYZ"
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Brooklyn, NY"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                placeholder="e.g., 10 PM - 4 AM"
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Event details..."
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ticket URL</Label>
              <Input
                value={formData.ticket_url}
                onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                placeholder="https://..."
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Cover Image URL</Label>
              <Input
                value={formData.cover_url}
                onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                placeholder="https://..."
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
            {saving ? 'Saving...' : 'Add Event'}
          </Button>
        </form>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">All Events ({events.length})</h2>
        {events.length > 0 ? (
          <div className="space-y-3">
            {events.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div>
                  <p className="font-bold">{event.title}</p>
                  <p className="text-sm text-zinc-400">{event.venue} • {event.date}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(event.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">No events yet</p>
        )}
      </div>
    </div>
  );
};

// Admin Photos Page
const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', event_name: '', instagram_url: '' });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title || '');
      data.append('event_name', formData.event_name || '');
      data.append('instagram_url', formData.instagram_url || '');
      data.append('image_file', imageFile);

      await axios.post(`${API_URL}/admin/photos`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo uploaded!');
      setFormData({ title: '', event_name: '', instagram_url: '' });
      setImageFile(null);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API_URL}/admin/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Manage Photos</h1>

      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#D9F99D]" />
          Upload Photo
        </h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Photo title"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Event Name</Label>
              <Input
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                placeholder="e.g., Summer Party 2024"
                className="bg-black/20 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Instagram Post URL</Label>
            <Input
              value={formData.instagram_url}
              onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
              placeholder="https://instagram.com/p/..."
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Image File *</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="bg-black/20 border-white/10"
            />
          </div>
          <Button type="submit" disabled={uploading} className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
            {uploading ? 'Uploading...' : 'Upload Photo'}
          </Button>
        </form>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">All Photos ({photos.length})</h2>
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${photo.image_url}`}
                  alt={photo.title || 'Photo'}
                  className="w-full aspect-square object-cover rounded-xl"
                />
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">No photos uploaded yet</p>
        )}
      </div>
    </div>
  );
};

// Admin Social Links Page
const AdminSocial = () => {
  const [links, setLinks] = useState({
    instagram: '', soundcloud: '', mixcloud: '', twitter: '', facebook: '', youtube: '', spotify: '', tiktok: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/social-links`);
      setLinks(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch social links:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/social-links`, links);
      toast.success('Social links updated!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const platforms = [
    { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
    { key: 'soundcloud', label: 'SoundCloud', placeholder: 'https://soundcloud.com/...' },
    { key: 'mixcloud', label: 'Mixcloud', placeholder: 'https://mixcloud.com/...' },
    { key: 'spotify', label: 'Spotify', placeholder: 'https://open.spotify.com/...' },
    { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...' },
    { key: 'twitter', label: 'Twitter/X', placeholder: 'https://twitter.com/...' },
    { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
    { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/...' },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Social Links</h1>

      <div className="glass-card rounded-2xl p-6">
        <div className="space-y-4">
          {platforms.map((platform) => (
            <div key={platform.key} className="space-y-2">
              <Label>{platform.label}</Label>
              <Input
                value={links[platform.key] || ''}
                onChange={(e) => setLinks({ ...links, [platform.key]: e.target.value })}
                placeholder={platform.placeholder}
                className="bg-black/20 border-white/10"
              />
            </div>
          ))}
          <Button onClick={handleSave} disabled={saving} className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Links'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Settings Page
const AdminSettings = () => {
  const [about, setAbout] = useState({ bio: '', tagline: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await axios.get(`${API_URL}/about`);
      setAbout(response.data);
    } catch (error) {
      console.error('Failed to fetch about:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/about`, null, {
        params: { bio: about.bio, tagline: about.tagline }
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Settings</h1>

      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">About / Bio</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tagline</Label>
            <Input
              value={about.tagline}
              onChange={(e) => setAbout({ ...about, tagline: e.target.value })}
              placeholder="e.g., NYC's Underground Sound"
              className="bg-black/20 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Bio</Label>
            <Textarea
              value={about.bio}
              onChange={(e) => setAbout({ ...about, bio: e.target.value })}
              placeholder="Tell your fans about yourself..."
              className="bg-black/20 border-white/10 min-h-[150px]"
            />
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Submissions Page
const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [playingId, setPlayingId] = useState(null);
  const audioRef = React.useRef(null);

  useEffect(() => {
    fetchSubmissions();
  }, [filter]);

  const fetchSubmissions = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/admin/submissions`
        : `${API_URL}/admin/submissions?status=${filter}`;
      const response = await axios.get(url);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    try {
      await axios.put(`${API_URL}/admin/submissions/${submissionId}/approve`);
      toast.success('Submission approved! Artist has been notified.');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to approve submission');
    }
  };

  const handleReject = async (submissionId) => {
    if (!window.confirm('Are you sure you want to reject this submission? The artist will be notified.')) return;
    try {
      await axios.put(`${API_URL}/admin/submissions/${submissionId}/reject`);
      toast.success('Submission rejected. Artist has been notified.');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to reject submission');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    try {
      await axios.delete(`${API_URL}/admin/submissions/${submissionId}`);
      toast.success('Submission deleted');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handlePlay = (submission) => {
    if (playingId === submission.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audioUrl = submission.audio_url.startsWith('http') 
        ? submission.audio_url 
        : `${process.env.REACT_APP_BACKEND_URL}${submission.audio_url}`;
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      audioRef.current.onended = () => setPlayingId(null);
      setPlayingId(submission.id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <Check className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Artist Submissions</h1>
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Mic2 className="w-4 h-4" />
          <span>{submissions.length} submissions</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((status) => (
          <Button
            key={status}
            onClick={() => setFilter(status)}
            className={`rounded-full px-4 py-2 text-sm ${
              filter === status
                ? 'bg-[#D9F99D] text-black'
                : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">Loading...</div>
      ) : submissions.length > 0 ? (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <div key={submission.id} className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  {/* Play Button */}
                  <Button
                    onClick={() => handlePlay(submission)}
                    className={`w-14 h-14 rounded-full flex-shrink-0 ${
                      playingId === submission.id
                        ? 'bg-[#D9F99D] text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {playingId === submission.id ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6 ml-1" />
                    )}
                  </Button>
                  <div>
                    <h3 className="text-xl font-bold">{submission.artist_name}</h3>
                    <p className="text-[#D9F99D]">{submission.track_title}</p>
                    <p className="text-zinc-400 text-sm">{submission.email}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${getStatusColor(submission.status)}`}>
                  {getStatusIcon(submission.status)}
                  {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <p className="text-sm"><span className="text-zinc-500">Genre:</span> {submission.genre || 'Not specified'}</p>
                  <p className="text-sm"><span className="text-zinc-500">Social:</span> {submission.social_media || 'Not provided'}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm"><span className="text-zinc-500">Submitted:</span> {new Date(submission.created_at).toLocaleDateString()}</p>
                  {submission.reviewed_at && (
                    <p className="text-sm"><span className="text-zinc-500">Reviewed:</span> {new Date(submission.reviewed_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              {submission.bio && (
                <div className="bg-white/5 rounded-xl p-4 mb-4">
                  <p className="text-sm text-zinc-500 mb-1">Bio:</p>
                  <p className="text-sm">{submission.bio}</p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                {submission.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => handleApprove(submission.id)}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReject(submission.id)}
                      variant="ghost"
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
                <a
                  href={`mailto:${submission.email}?subject=RE: Your Music Submission - ${submission.track_title}`}
                  className="ml-auto"
                >
                  <Button variant="outline" className="bg-white/5 border-white/10 hover:bg-white/10">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Artist
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  onClick={() => handleDelete(submission.id)}
                  className="text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Mic2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">No {filter !== 'all' ? filter : ''} submissions</h3>
          <p className="text-zinc-400">When artists submit their music, they'll appear here for your review.</p>
        </div>
      )}
    </div>
  );
};

// Admin Bookings Page
const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking marked as ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Delete this booking request?')) return;
    try {
      await axios.delete(`${API_URL}/admin/bookings/${bookingId}`);
      toast.success('Booking deleted');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400';
      case 'declined': return 'bg-red-500/20 text-red-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <Check className="w-4 h-4" />;
      case 'declined': return <X className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Booking Requests</h1>
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Mail className="w-4 h-4" />
          <span>{bookings.length} requests</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">Loading...</div>
      ) : bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">{booking.name}</h3>
                  <p className="text-zinc-400 text-sm">{booking.email} • {booking.phone || 'No phone'}</p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${getStatusColor(booking.status)}`}>
                  {getStatusIcon(booking.status)}
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <p className="text-sm"><span className="text-zinc-500">Event Type:</span> <span className="text-[#D9F99D]">{booking.event_type}</span></p>
                  <p className="text-sm"><span className="text-zinc-500">Date:</span> {booking.event_date}</p>
                  <p className="text-sm"><span className="text-zinc-500">Venue:</span> {booking.venue_location || 'Not specified'}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm"><span className="text-zinc-500">Budget:</span> {booking.budget_range || 'Not specified'}</p>
                  <p className="text-sm"><span className="text-zinc-500">Submitted:</span> {new Date(booking.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {booking.message && (
                <div className="bg-white/5 rounded-xl p-4 mb-4">
                  <p className="text-sm text-zinc-500 mb-1">Message:</p>
                  <p className="text-sm">{booking.message}</p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                {booking.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => handleStatusChange(booking.id, 'confirmed')}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Confirm
                    </Button>
                    <Button
                      onClick={() => handleStatusChange(booking.id, 'declined')}
                      variant="ghost"
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </>
                )}
                <a
                  href={`mailto:${booking.email}?subject=RE: DJ Shadow Booking - ${booking.event_type}`}
                  className="ml-auto"
                >
                  <Button variant="outline" className="bg-white/5 border-white/10 hover:bg-white/10">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  onClick={() => handleDelete(booking.id)}
                  className="text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Send className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">No booking requests yet</h3>
          <p className="text-zinc-400">When fans submit booking requests, they'll appear here.</p>
        </div>
      )}
    </div>
  );
};

export default Admin;
